import React, { useState } from 'react';
import Card from '../common/Card';
import { theme } from '../../theme';
import { HousingQuestionsPictogram } from '../../assets';
import { Body2 } from '../common/Text';
import styled from 'styled-components/macro';
import { useMessageGetter } from 'react-message-context';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import ReactGA from 'react-ga';
import { showNotification } from '../../context/appActions';
import { NotificationType, useAppDispatch, useAppState } from '../../context/appContext';
import { Button } from '../common';
import { DeviceSize } from '../basic/DeviceSize';
import FormInstructions from './FormInstructions';
import { InputField, TextAreaElement } from '../common/forms';
import { zodResolver } from '@hookform/resolvers/zod';
import { ContactFormState, schema } from './schema';
import { DropdownOptionValue } from '../common/Dropdown';
import { CategoryDropdown, MainContactCategory } from './CategoryDropdown';
import { SubcategoryDropdown } from './SubcategoryDropdown';
import FormError from '../common/forms/FormError';
import { postContactRequest } from '../../apis_kenno';

const FormInfo = styled(Body2)`
  width: 50%;

  @media ${DeviceSize.desktopS} {
    width: 100%;
  }
`;

const FormContentContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media ${DeviceSize.mobileL} {
    display: flex;
    flex-direction: column;
  }
`;

const ContactForm: React.FC = () => {
  const appDispatch = useAppDispatch();
  const t = useMessageGetter('');
  const translationPrefix = 'forms.contact';
  const { language } = useAppState();
  const [isBusy, setIsBusy] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const formMethods = useForm<ContactFormState>({
    resolver: zodResolver(schema),
    defaultValues: {
      category: MainContactCategory.CUSTOMER_SERVICE,
      subject: '',
      content: '',
      // Add language for selecting correct email response template in backend
      language,
    },
  });

  const { watch, reset, handleSubmit } = formMethods;
  const category = watch('category');
  const subcategory = watch('subcategory');

  const onSubmit: SubmitHandler<ContactFormState> = async (data) => {
    setSubmitFailed(false);
    setErrorMessage('');
    setIsBusy(true);

    try {
      await postContactRequest(data);
      await showNotification(appDispatch, t('messages.new.form.messagesent'), NotificationType.SUCCESS);
      handleEmptyForm();

      ReactGA.event({
        category: `New message ${t(`form.categories.${data.category}`)}`,
        action: `Submit subject: ${data.subject}`,
        label: data.content,
      });
    } catch (error) {
      console.error(error);
      setSubmitFailed(true);
      setErrorMessage(t('messages.new.form.messagefailure'));
    } finally {
      setIsBusy(false);
    }
  };

  const disableSubmit =
    subcategory === 'customerservice.faultreports' || subcategory === 'customerservice.sauna_laundry_parking';

  const handleEmptyForm = () => {
    reset();
  };

  return (
    <FormProvider {...formMethods}>
      <Card title={t('messages.new.title')} Pictogram={HousingQuestionsPictogram} background={theme().colors.mint}>
        <FormInfo>{t('messages.new.info')}</FormInfo>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormContentContainer>
            <div>
              <InputField<ContactFormState, DropdownOptionValue>
                fieldPath="category"
                translationPrefix={translationPrefix}
                required
                dark
                inputElementRenderer={(props) => <CategoryDropdown {...props} />}
              />
              <InputField<ContactFormState, DropdownOptionValue>
                fieldPath="subcategory"
                translationPrefix={translationPrefix}
                required
                dark
                inputElementRenderer={(props) => <SubcategoryDropdown {...props} category={category} />}
              />
            </div>
            <FormInstructions subcategory={subcategory} showOnMobileOnly />
            <div>
              <InputField<ContactFormState, string>
                fieldPath="subject"
                translationPrefix={translationPrefix}
                placeholder={t(`${translationPrefix}.subject`)}
                type="text"
                required
                dark
              />
              <InputField<ContactFormState, string>
                fieldPath="content"
                translationPrefix={translationPrefix}
                required
                dark
                inputElementRenderer={(props) => (
                  <TextAreaElement {...props} placeholder={t(`${translationPrefix}.contentPlaceholder`)} />
                )}
              />
            </div>
          </FormContentContainer>
          <FormInstructions subcategory={subcategory} showOnMobileOnly={false} />
          {submitFailed && <FormError message={errorMessage} />}
          <Button type="submit" primary icon="send" disabled={disableSubmit || isBusy}>
            {t('messages.new.form.button')}
          </Button>
        </form>
      </Card>
    </FormProvider>
  );
};

export default ContactForm;
